import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/**
 * Sentry instrumentation for the learner playback flow.
 *
 * Learners report "stuck" and "repeating" videos far more often than any error reaches us, because
 * most of the underlying mechanisms (a fullscreen seek snapped back, a tab restored from suspension,
 * a progress write that never resolved) do not throw. This service records those moments as
 * breadcrumbs, so they show up on any later event or replay, and promotes the ones worth counting on
 * their own to warning-level messages tagged `area:playback`, filterable by device in Sentry.
 */
@Injectable({
	providedIn: 'root'
})
export class PlaybackDiagnosticsService {

	private static readonly SNAPBACK_WARN_THRESHOLD = 3;
	private static readonly CATEGORY = 'playback';

	private snapbacks = 0;
	private snapbackReported = false;
	private chapter: Record<string, string | number | null> = {};

	setChapterContext(context: { categoryId?: string; courseId?: string; chapterId?: string; chapterIndex?: number; videoId?: string }): void {
		this.chapter = {
			categoryId: context.categoryId ?? null,
			courseId: context.courseId ?? null,
			chapterId: context.chapterId ?? null,
			chapterIndex: context.chapterIndex ?? null,
			videoId: context.videoId ?? null,
		};
		Sentry.setContext('chapter', this.chapter);
	}

	// A new video source starts a fresh snap-back count; the threshold is per video, not per visit.
	videoSessionStarted(videoId: string, engine: 'hls.js' | 'native-hls' | 'progressive' | 'none', data: Record<string, unknown> = {}): void {
		this.snapbacks = 0;
		this.snapbackReported = false;
		this.breadcrumb('video source loaded', { videoId, engine, ...data });
	}

	breadcrumb(message: string, data: Record<string, unknown> = {}): void {
		Sentry.addBreadcrumb({
			category: PlaybackDiagnosticsService.CATEGORY,
			level: 'info',
			message,
			data: { ...this.environment(), ...data },
		});
	}

	warn(message: string, data: Record<string, unknown> = {}, tags: Record<string, string> = {}): void {
		Sentry.captureMessage(message, {
			level: 'warning',
			extra: { ...this.environment(), ...this.chapter, ...data },
			tags: { area: PlaybackDiagnosticsService.CATEGORY, ...tags },
		});
	}

	error(error: unknown, message: string, data: Record<string, unknown> = {}, tags: Record<string, string> = {}): void {
		const exception = error instanceof Error ? error : new Error(`${message}: ${describe(error)}`);
		Sentry.captureException(exception, {
			extra: { ...this.environment(), ...this.chapter, reason: message, rawError: describe(error), ...data },
			tags: { area: PlaybackDiagnosticsService.CATEGORY, ...tags },
		});
	}

	// The seek guard rewound the learner. A few are normal (a mis-tap on the scrub bar); a run of them
	// is the "the video keeps going back" complaint happening live.
	seekSnappedBack(from: number, to: number, source: string): void {
		this.snapbacks++;
		this.breadcrumb('seek snapped back', { from, to, source, snapbacks: this.snapbacks });
		if (this.snapbacks >= PlaybackDiagnosticsService.SNAPBACK_WARN_THRESHOLD && !this.snapbackReported) {
			this.snapbackReported = true;
			this.warn('Video seek snapped back repeatedly', { from, to, source, snapbacks: this.snapbacks }, { event: 'seek-snapback' });
		}
	}

	private environment(): Record<string, unknown> {
		const doc: any = typeof document !== 'undefined' ? document : null;
		return {
			online: typeof navigator !== 'undefined' ? navigator.onLine : null,
			visibility: doc?.visibilityState ?? null,
			fullscreen: !!(doc?.fullscreenElement || doc?.webkitFullscreenElement),
		};
	}
}

function describe(error: unknown): string {
	if (error instanceof Error) {
		return `${error.name}: ${error.message}`;
	}
	try {
		return typeof error === 'string' ? error : JSON.stringify(error);
	} catch {
		return String(error);
	}
}
